import * as Icons from '@ant-design/icons';
import { Button, Drawer, Flex, Image, Skeleton, Typography } from 'antd';
import { IDocument } from 'interfaces/documentation/document';
import { download } from 'services/documents';
import { useCallback, useState, ReactNode } from 'react';

interface DocumentViewerProps {
  document: IDocument;
  children: ReactNode;
  drawerTitle?: string;
  drawerWidth?: string | number;
}

export const DocumentViewer = ({ 
  document, 
  children, 
  drawerTitle = 'Visualizar documento',
  drawerWidth = '50%'
}: DocumentViewerProps) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [loadingFile, setLoadingFile] = useState(false);

  const getFile = useCallback(async (documentId: string) => {
    try {
      setLoadingFile(true);
      const fileData = await download(documentId);
      setFileUrl(fileData.url);
    } catch (error) {
      console.error('Erro ao recuperar arquivo:', error);
    } finally {
      setLoadingFile(false);
    }
  }, []);

  const handleViewFile = useCallback(() => {
    setOpenDrawer(true);
    getFile(document.id);
  }, [document.id, getFile]);

  const handleCloseDrawer = useCallback(() => {
    setOpenDrawer(false);
    setFileUrl(''); // Limpar URL ao fechar
  }, []);

  return (
    <>
      <div onClick={handleViewFile} style={{ cursor: 'pointer' }}>
        {children}
      </div>

      <Drawer
        className="!p-0"
        title={
          <Flex vertical>
            <Typography.Title level={4} className="!mb-0">
              {drawerTitle}
            </Typography.Title>
            <Typography.Text type="secondary" className="font-normal">
              {document.file_name}
            </Typography.Text>
          </Flex>
        }
        extra={
          <Button
            variant="text"
            color="default"
            icon={<Icons.CloseOutlined />}
            onClick={handleCloseDrawer}
          />
        }
        closable={false}
        closeIcon={false}
        onClose={handleCloseDrawer}
        open={openDrawer}
        width={drawerWidth}
      >
        <div className="relative flex items-center justify-center min-h-full">
          {loadingFile ? (
            <Skeleton.Image active className="!w-full h-full min-h-[500px]" />
          ) : (
            <>
              {document.file_name.includes('pdf') ? (
                <iframe
                  src={fileUrl}
                  className="absolute top-0 left-0 overflow-y-auto overflow-x-hidden w-full h-full flex-1"
                />
              ) : (
                <Image src={fileUrl} />
              )}
            </>
          )}
        </div>
      </Drawer>
    </>
  );
};
